<script lang="ts">
  import type {Poem, UserExtended} from '../types'
  import api from '../api/api'
  import {replaceValues, t} from '../i18n'
  import LoadMore from '../shared/LoadMore.svelte'
  import Placeholder from '../shared/Placeholder.svelte'
  import SmallPoem from '../poems/SmallPoem.svelte'
  import SpinnerSection from '../shared/SpinnerSection.svelte'
  import {user as currentUser} from '../stores/user'
  import {modal} from '../stores/modal'
  import UserFollowers from './UserFollowers.svelte'
  import {Link, navigate} from 'svelte5-router'
  import NotFoundPage from '../../src/NotFoundPage.svelte'

  export let handle: string
  let user: UserExtended | undefined
  let descriptionOpened = false
  let poems: Poem[] | undefined
  let type = new URLSearchParams(window.location.search).get('type') || ''
  let descriptionDiv: HTMLDivElement
  let loaded = false

  $: handle && (user = undefined) // reload when handle changes

  $: types = {
    '': {
      label: t.users.all_poems.title,
      url: `/authors/${user?.id}`,
      fetchUrl: `/users/${user?.id}/poems`,
      count: user?.poems_count
    },
    'originals': {
      label: t.users.poems.title,
      url: `/authors/${user?.id}?type=originals`,
      fetchUrl: `/users/${user?.id}/poems?original=true`,
      count: user?.originals_count
    },
    'translations': {
      label: t.users.translations.title,
      url: `/authors/${user?.id}?type=translations`,
      fetchUrl: `/users/${user?.id}/poems?original=false`,
      count: user?.translations_count
    }
  }

  async function load(handle: string) {
    loaded = false
    user = await api.get<UserExtended>(`/users/${handle}`).catch(() => undefined).finally(() => loaded = true)
    if (!user) return
    user.description = user.description?.replace(/\n/g, '<br />') || ''
    user.website = !user.website || user.website.startsWith('http')
      ? user.website
      : `http://${user.website}`
  }

  $: if (handle) load(handle)

  async function follow() {
    if (!user) return
    await api.put(`/users/${user.id}/follow`)
    user.is_followed = true
    user.followers_count += 1
  }

  async function unfollow() {
    if (!user) return
    await api.put(`/users/${user.id}/unfollow`)
    user.is_followed = false
    user.followers_count -= 1
  }

  function openFollowers() {
    modal.show(UserFollowers, {props: {user}})
  }

  $: descriptionFits = user?.description && (!descriptionDiv || descriptionDiv.scrollHeight <= 150)

  function toggleDescription() {
    descriptionOpened = !descriptionOpened
  }

  $: userLocation = user ?
    [user.country_code ? t.countries[user.country_code] : undefined, user.city].filter(i => !!i).join(' | ') :
    undefined

  function changePoemType(e: Event, url: string, poemType: string) {
    e.preventDefault()
    type = poemType
    poems = undefined
    navigate(url)
  }
</script>

{#if user}
  <div class="row">
    <div class="profile__header">
      <div class="profile__info">
        <div class="profile__contacts info_cell">
          {#if user.facebook_profile}
            <a class="soc-icon socicon-facebook" href={user.facebook_profile} aria-label="Facebook" target="_blank"/>
          {/if}
          {#if user.instagram_profile}
            <a class="soc-icon socicon-instagram" href={user.instagram_profile} aria-label="Instagram" target="_blank"/>
          {/if}
          {#if user.vk_profile}
            <a class="soc-icon socicon-vk" href={user.vk_profile} aria-label="VK" target="_blank"/>
          {/if}
          {#if user.website}
            <a class="personal-site" href={user.website} target="_blank">{user.website}</a>
          {/if}
        </div>
        <div class="profile__user info_cell">
          <div class="profile__photo">
            <img src={user.avatar_big_thumgb} alt={user.full_name}/>
          </div>
          <p class="profile__name">{user.full_name}</p>

          {#if userLocation}
            <span class="profile__location italic">
              {userLocation}
            </span>
          {/if}
        </div>

        <div class="profile-follow profile-follow--normal info_cell">
          {#if $currentUser}
            {#if $currentUser.id === user.id}
              <button class="ui button black" onclick={openFollowers}>
                {replaceValues(t.user.followers, {n: user.followers_count})}
                <span class="followers-count">{user.followers_count}</span>
              </button>
            {:else}
              {#if user.is_followed}
                <button class="ui button black" onclick={unfollow}>
                  {t.users.unfollow} <span class="followers-count">{user.followers_count}</span>
                </button>
              {:else}
                <button class="ui button black" onclick={follow}>
                  {t.users.follow} <span class="followers-count">{user.followers_count}</span>
                </button>
              {/if}
            {/if}
          {/if}
        </div>
      </div>

      {#if user.description}
        <div class="profile__description">
          <div class="description {descriptionOpened ? 'opened' : 'closed'}"
               bind:this={descriptionDiv}>{@html user.description}</div>

          {#if !descriptionFits}
            {#if !descriptionOpened}
              <div class="continue-read" onclick={toggleDescription}>
                {t.users.description.continue}
              </div>
            {:else}
              <div class="continue-read" onclick={toggleDescription}>
                {t.users.description.hide}
              </div>
            {/if}
          {/if}
        </div>
      {/if}
    </div>

    <div class="grid-section">
      <div class="poems__tabs">
        {#each Object.entries(types) as [type, {url, label, count}]}
          <Link class="tab__item" to={url} onclick={e => changePoemType(e, url, type)}>
            {label}
            <span class="followers-count">{count}</span>
          </Link>
        {/each}
      </div>

      <div class="grid-section">
        {#if poems && poems.length}
          <div class="grid three">
            {#each poems as poem}
              <div class="grid__item">
                <SmallPoem {poem}/>
              </div>
            {/each}
          </div>
        {:else}
          <Placeholder placeholder={t.placeholder.no_author_poems} loading={!poems}/>
        {/if}
      </div>

      <LoadMore bind:items={poems} url={types[type].fetchUrl}/>
    </div>
  </div>
{:else if loaded}
  <NotFoundPage/>
{:else}
  <SpinnerSection/>
{/if}
